import { Component, ChangeDetectionStrategy, EventEmitter, Input, Output, signal, OnInit, inject, computed, ElementRef, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { Router } from '@angular/router';
import { CommonService } from '../../../core/services/common';
import { TokenService } from '../../../core/services/token';

export interface MenuItem {
  label: string;
  icon?: string;
  path: string;
  permission?: string;
  children?: MenuItem[];
  isSpecial?: boolean;
}

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './sidebar.html',
  styleUrls: ['./sidebar.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SidebarComponent implements OnInit {
  @Input() isCollapsed = false;
  hover = false;
  showSettingsMenu = signal(false);

  @Output() closeSidebar = new EventEmitter<void>();
  @Output() toggleSidebarParent = new EventEmitter<void>();
  @Output() openChangePassword = new EventEmitter<void>();
  @Output() sidebarToggled = new EventEmitter<boolean>();
  @Output() openSearch = new EventEmitter<void>();
  @Output() currentMenu=new EventEmitter<string>();
  private router = inject(Router)

  common=inject(CommonService);
  private tokenService = inject(TokenService);
  private elementRef = inject(ElementRef);

  expandedIndex = signal<number | null>(null);

  menu: MenuItem[] = [
    /*{
      label: 'Users',
      icon: 'User',
      path: 'user'
    },*/
    {
      label: 'Meetings',
      icon: 'Meeting',
      path: 'meeting'
    },
    {
      label: 'Meeting BotSetup',
      icon: 'meeting-bot',
      path: 'bot-setup',
    },
    {
      label: 'History',
      icon: 'history',
      path: 'history',
    }
  ];


  ngOnInit() {
    this.updateMenu(this.router.url);

    // watch for route changes
    this.router.events.subscribe(() => {
      this.updateMenu(this.router.url);
    });


  }
  openSearchPopup() {
    console.log("event emitted");
    this.openSearch.emit();
  }
  private updateMenu(url: string) {
    this.menu = [...this.menu];
  }


  filterMenuByPermission(menu: MenuItem[], profile: any): MenuItem[] {
    if (profile?.is_admin) return menu;
    const authItems = profile?.roles?.auth_items || {};
    return menu
      .filter(item =>
        !item.permission ||
        Object.keys(authItems).some(moduleKey => item.permission && authItems[moduleKey]?.[item.permission])
      )
      .map(item => ({
        ...item,
        children: item.children ? this.filterMenuByPermission(item.children, profile) : undefined
      }));
  }

  toggleSubmenu(item:any,idx: number) {
    this.common.setHeading(item.label);
    if (this.isCollapsed) {
      return;
    }
    this.expandedIndex.set(this.expandedIndex() === idx ? null : idx);
  }

  toggleSidebar() {
    this.isCollapsed = !this.isCollapsed;
    this.sidebarToggled.emit(this.isCollapsed);
    if (this.isCollapsed) {
      this.expandedIndex.set(null);
    }
    this.onToggleClick();
  }

  onToggleClick() {
    this.toggleSidebarParent.emit();
  }

  onCloseSidebar() {
    this.closeSidebar.emit();
  }

  openChangePasswordDialog() {
    this.openChangePassword.emit();
  }
  isActive(item: any): boolean {
    const currentUrl = this.router.url;
    const itemPath = `/${item.path}`;
    
    // Check for exact match or if current URL starts with the item path
    return currentUrl === itemPath || currentUrl.startsWith(itemPath + '/');
  }
  isActivePrefix(path: string): boolean {
    return this.router.url.startsWith(path);
  }

  onItemClick(item: MenuItem, index: number) {
    if (this.isCollapsed && item.children) {
      this.toggleSidebarParent.emit();
      setTimeout(() => this.toggleSubmenu(item,index), 100);
    } else {
      this.toggleSubmenu(item,index);
    }
  }

  toggleSettingsMenu() {
    this.showSettingsMenu.set(!this.showSettingsMenu());
  }

  logout() {
    this.tokenService.clearToken();
    this.showSettingsMenu.set(false);
    this.router.navigate(['/']);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (this.showSettingsMenu()) {
      // Check if click is inside the component
      // We specifically want to check if it's inside the settings menu container
      // But checking 'elementRef.nativeElement.contains' might be enough if the sidebar catches clicks.
      // However, the sidebar is always present. We only want to close if the click is OUTSIDE the sidebar 
      // OR if it's on a part of the sidebar that isn't the menu itself?
      // Actually, standard behavior: if I click outside the *menu*, it should close.
      // Since the menu is part of the sidebar, if I click elsewhere in the sidebar, should it close?
      // Probably yes, usually "click outside the menu/trigger" closes it.
      
      const target = event.target as HTMLElement;
      // We need a specific reference to the toggle button + menu container.
      // Since we don't have ViewChild for it yet, let's check if the click target 
      // is contained within the component.
      // But strictly, we want: !this.elementRef.nativeElement.contains(target)
      // Wait, if I click the sidebar toggle (top), I probably want the settings menu (bottom) to close?
      // If I click the main content, I want it to close.
      
      // Let's use a specific class check or assume close on ANY click that isn't inside the settings area.
      // For now, let's close if !this.elementRef.nativeElement.contains(target).
      // But that only handles clicks outside the SIDEBAR.
      // If I click the "Users" link while settings is open, does it close? 
      // Ideally yes.
      
      const clickedInside = this.elementRef.nativeElement.contains(target);
      const isSettingsClick = target.closest('.settings-container'); // We'll add this class to HTML
      
      if (!isSettingsClick) {
        this.showSettingsMenu.set(false);
      }
    }
  }
}